/**
 * O andamento do caso que se cadastra sozinho (E189.F2, ADR-0138 §4): relê enquanto o pedido está
 * `cadastrando`. Quando o pedido some depois de ter estado em andamento, o sistema concluiu, e
 * `onConcluido` avisa (o Conferir lê a conclusão e segue para o Acompanhar). A falha de rede de uma
 * releitura do caso cadastrando não interrompe: a próxima tenta de novo. A falha antes disso deixa o
 * Conferir de sempre. A parada (`precisa_de_conferencia`) para de reler.
 *
 * Depois do "Enviar e seguir" (`recarregar`), o pedido já foi gravado: se ele some antes da primeira
 * releitura, o sistema concluiu nesse meio-tempo, e `onConcluido` avisa do mesmo jeito (E193.F1.C13).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { lerAndamento, type AndamentoDoCadastroData } from '@/lib/cadastro_sem_espera';

export const INTERVALO_DO_ANDAMENTO_MS = 3000;

export interface AndamentoComReleitura {
  /** `undefined` antes da primeira leitura; `null` sem pedido. */
  andamento: AndamentoDoCadastroData | null | undefined;
  /** Relê agora e retoma as releituras (depois do "Enviar e seguir", por exemplo). */
  recarregar: () => void;
}

export function useAndamentoDoCadastro(
  client: AxiosInstance,
  caseId: string,
  onConcluido: () => void,
): AndamentoComReleitura {
  const [andamento, setAndamento] = useState<AndamentoDoCadastroData | null | undefined>(undefined);
  const [rodada, setRodada] = useState(0);
  const avisar = useRef(onConcluido);
  avisar.current = onConcluido;

  useEffect(() => {
    let ativo = true;
    let espera: ReturnType<typeof setTimeout> | undefined;
    let estavaCadastrando = rodada > 0;
    const ciclo = async (): Promise<void> => {
      let lido: AndamentoDoCadastroData | null | 'falhou';
      try {
        lido = await lerAndamento(client, caseId);
      } catch {
        lido = 'falhou';
      }
      if (!ativo) return;
      if (lido === 'falhou') {
        // Sem ter visto o cadastro em andamento, a tela segue com o Conferir de sempre.
        if (!estavaCadastrando) {
          setAndamento((anterior) => (anterior === undefined ? null : anterior));
          return;
        }
      } else {
        setAndamento(lido);
        if (lido === null && estavaCadastrando) avisar.current();
        estavaCadastrando = lido?.situacao === 'cadastrando';
        if (!estavaCadastrando) return;
      }
      espera = setTimeout(() => void ciclo(), INTERVALO_DO_ANDAMENTO_MS);
    };
    void ciclo();
    return () => {
      ativo = false;
      if (espera !== undefined) clearTimeout(espera);
    };
  }, [client, caseId, rodada]);

  const recarregar = useCallback(() => setRodada((r) => r + 1), []);
  return { andamento, recarregar };
}
